import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    Stack,
    TextField,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Alert,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Divider,
    Grid
} from '@mui/material';
import { styled } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import ImageIcon from '@mui/icons-material/Image';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ApiService from '../../../services/api';

const PageContainer = styled(Box)(({ theme }) => ({
    padding: theme.spacing(3),
    backgroundColor: '#f5f7fa',
    minHeight: '100vh'
}));

const TitleSection = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
}));

const TitleIcon = styled(Box)(({ theme }) => ({
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
}));

const HeaderSection = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
    flexWrap: 'wrap',
    gap: '16px'
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
    borderRadius: theme.spacing(1.5),
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    overflow: 'hidden'
}));

const StatusChip = styled(Chip)(({ status }) => {
    const colors = {
        pending_payment: { bg: '#fff7ed', color: '#c2410c', border: '#ffedd5' },
        pending_creation: { bg: '#eff6ff', color: '#1d4ed8', border: '#dbeafe' },
        in_progress: { bg: '#fefce8', color: '#a16207', border: '#fef9c3' },
        completed: { bg: '#f0fdf4', color: '#15803d', border: '#dcfce7' },
        cancelled: { bg: '#fef2f2', color: '#b91c1c', border: '#fee2e2' }
    };
    const style = colors[status] || colors.pending_creation;
    return {
        backgroundColor: style.bg,
        color: style.color,
        fontWeight: 600,
        textTransform: 'capitalize',
        border: `1px solid ${style.border}`,
        borderRadius: '8px',
        fontSize: '0.75rem',
        height: '24px'
    };
});

const TableRowStyled = styled(TableRow)(({ theme }) => ({
    '&:hover': {
        backgroundColor: 'rgba(59, 130, 246, 0.02) !important',
        transition: 'background-color 0.2s ease',
    },
    '& td': {
        borderBottom: '1px solid #f1f5f9',
        padding: '16px'
    }
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
    padding: '8px',
    borderRadius: '8px',
    transition: 'all 0.2s ease',
    '&:hover': {
        backgroundColor: '#eff6ff',
        color: '#3b82f6',
    }
}));

const DetailSection = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(3)
}));

const DetailTitle = styled(Typography)(({ theme }) => ({
    fontWeight: 600,
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5)
}));

const DetailItem = styled(Box)(({ theme }) => ({
    display: 'flex',
    padding: '8px 0',
    borderBottom: '1px solid #f8fafc',
    '&:last-child': {
        borderBottom: 'none'
    },
    '& .label': {
        width: 160,
        color: '#64748b',
        fontSize: '0.875rem',
        fontWeight: 500
    },
    '& .value': {
        flex: 1,
        color: '#1e293b',
        fontSize: '0.875rem',
        fontWeight: 500
    }
}));

const InfoCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2.5),
    borderRadius: '16px',
    border: '1px solid #e2e8f0',
    boxShadow: 'none',
    height: '100%',
    backgroundColor: '#fff'
}));

const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const formatStatus = (status) => {
    return (status || '').replace(/_/g, ' ');
};

const GmbCreationRequests = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [total, setTotal] = useState(0);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    // View dialog
    const [viewDialog, setViewDialog] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    // Edit status dialog
    const [editDialog, setEditDialog] = useState(false);
    const [editStatus, setEditStatus] = useState('');
    const [editNotes, setEditNotes] = useState('');
    const [saving, setSaving] = useState(false);

    const fetchRequests = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({
                page: page + 1,
                pageSize: rowsPerPage
            });
            if (search) params.append('search', search);
            if (statusFilter) params.append('status', statusFilter);

            const response = await ApiService.get(`/admin/gmb-creation/list?${params}`);
            const data = response?.data?.data || response?.data || {};
            setRequests(data.requests || []);
            setTotal(data.total || 0);
        } catch (err) {
            console.error('Error fetching GMB requests:', err);
            setError('Failed to load GMB creation requests');
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, search, statusFilter]);

    useEffect(() => {
        fetchRequests();
    }, [fetchRequests]);

    const handleViewDetails = async (request) => {
        setSelectedRequest(request);
        setViewDialog(true);
        setDetailLoading(true);

        try {
            const response = await ApiService.get(`/admin/gmb-creation/${request.id}`);
            const data = response?.data?.data || response?.data || {};
            setSelectedRequest(data.request || request);
        } catch (err) {
            console.error('Error fetching request details:', err);
        } finally {
            setDetailLoading(false);
        }
    };

    const handleEditClick = (request) => {
        setSelectedRequest(request);
        setEditStatus(request.status || 'pending_creation');
        setEditNotes(request.admin_notes || '');
        setEditDialog(true);
    };

    const handleSaveStatus = async () => {
        if (!selectedRequest) return;
        setSaving(true);

        try {
            await ApiService.put(`/admin/gmb-creation/${selectedRequest.id}/status`, {
                status: editStatus,
                admin_notes: editNotes
            });
            setEditDialog(false);
            fetchRequests();
        } catch (err) {
            console.error('Error updating status:', err);
            alert('Failed to update status');
        } finally {
            setSaving(false);
        }
    };

    const handleSearch = () => {
        setPage(0);
        fetchRequests();
    };

    return (
        <PageContainer>
            <HeaderSection>
                <TitleSection>
                    <TitleIcon>
                        <BusinessIcon fontSize="large" />
                    </TitleIcon>
                    <Box>
                        <Typography variant="h4" fontWeight={700} color="#1f2937">
                            GMB Creation Requests
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            View and manage GMB profile creation requests from users
                        </Typography>
                    </Box>
                </TitleSection>
                <Button
                    startIcon={<RefreshIcon />}
                    onClick={fetchRequests}
                    variant="outlined"
                    sx={{
                        borderRadius: '10px',
                        textTransform: 'none',
                        fontWeight: 600,
                        borderColor: '#3b82f6',
                        color: '#3b82f6',
                        '&:hover': {
                            borderColor: '#2563eb',
                            backgroundColor: 'rgba(59, 130, 246, 0.04)'
                        }
                    }}
                >
                    Refresh
                </Button>
            </HeaderSection>

            {/* Filters */}
            <Stack direction="row" spacing={2} mb={3} alignItems="center">
                <TextField
                    size="small"
                    placeholder="Search by business, name, email..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    InputProps={{
                        startAdornment: <SearchIcon sx={{ color: '#3b82f6', mr: 1 }} />
                    }}
                    sx={{
                        minWidth: 300,
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: '#fff',
                            borderRadius: '10px',
                            '&:hover fieldset': { borderColor: '#3b82f6' },
                            '&.Mui-focused fieldset': { borderColor: '#3b82f6' }
                        }
                    }}
                />
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                        value={statusFilter}
                        label="Status"
                        onChange={(e) => {
                            setStatusFilter(e.target.value);
                            setPage(0);
                        }}
                        sx={{
                            backgroundColor: '#fff',
                            borderRadius: '10px',
                        }}
                    >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="pending_payment">Pending Payment</MenuItem>
                        <MenuItem value="pending_creation">Pending Creation</MenuItem>
                        <MenuItem value="in_progress">In Progress</MenuItem>
                        <MenuItem value="completed">Completed</MenuItem>
                        <MenuItem value="cancelled">Cancelled</MenuItem>
                    </Select>
                </FormControl>
                <Button
                    variant="contained"
                    onClick={handleSearch}
                    sx={{
                        borderRadius: '10px',
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 3,
                        backgroundColor: '#3b82f6',
                        '&:hover': { backgroundColor: '#2563eb' }
                    }}
                >
                    Search
                </Button>
            </Stack>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <StyledPaper>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                                <TableCell sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>ID</TableCell>
                                <TableCell sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Business Name</TableCell>
                                <TableCell sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Contact</TableCell>
                                <TableCell sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>City</TableCell>
                                <TableCell sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</TableCell>
                                <TableCell sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Date</TableCell>
                                <TableCell align="center" sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                                        <CircularProgress size={30} sx={{ color: '#3b82f6' }} />
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Loading requests...</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : requests.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                                        <Box sx={{ opacity: 0.5, mb: 1 }}>
                                            <AssignmentIcon sx={{ fontSize: 48 }} />
                                        </Box>
                                        <Typography color="text.secondary">
                                            No GMB creation requests found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                requests.map((req) => (
                                    <TableRowStyled key={req.id}>
                                        <TableCell sx={{ color: '#64748b', fontWeight: 600 }}>#{req.id}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={700} color="#1e293b">
                                                {req.business_name}
                                            </Typography>
                                            {req.business_category && (
                                                <Typography variant="caption" sx={{ color: '#64748b', display: 'flex', alignItems: 'center' }}>
                                                    {req.business_category}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={600} color="#334155">{req.contact_name}</Typography>
                                            <Typography variant="caption" sx={{ color: '#64748b' }}>
                                                {req.contact_email}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ color: '#475569', fontWeight: 500 }}>{req.city}</TableCell>
                                        <TableCell>
                                            <StatusChip
                                                label={formatStatus(req.status)}
                                                status={req.status}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ color: '#64748b', fontSize: '0.8125rem', fontWeight: 500 }}>
                                            {formatDate(req.created_at)}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Stack direction="row" spacing={0.5} justifyContent="center">
                                                <ActionButton
                                                    onClick={() => handleViewDetails(req)}
                                                    size="small"
                                                    title="View Details"
                                                >
                                                    <VisibilityIcon fontSize="small" />
                                                </ActionButton>
                                                <ActionButton
                                                    onClick={() => handleEditClick(req)}
                                                    size="small"
                                                    title="Edit Status"
                                                >
                                                    <EditIcon fontSize="small" />
                                                </ActionButton>
                                            </Stack>
                                        </TableCell>
                                    </TableRowStyled>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50]}
                />
            </StyledPaper>

            {/* View Details Dialog */}
            <Dialog
                open={viewDialog}
                onClose={() => setViewDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Typography variant="h6" fontWeight={700}>
                        GMB Creation Request Details
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    {detailLoading ? (
                        <Box textAlign="center" py={8}>
                            <CircularProgress size={40} sx={{ color: '#3b82f6' }} />
                        </Box>
                    ) : selectedRequest && (
                        <Grid container spacing={3} sx={{ mt: 0.5 }}>
                            <Grid item xs={12} md={6}>
                                <InfoCard sx={{ mb: 3 }}>
                                    <DetailTitle>
                                        <BusinessIcon sx={{ color: '#3b82f6' }} /> Business Information
                                    </DetailTitle>
                                    <DetailItem>
                                        <span className="label">Business Name</span>
                                        <span className="value">{selectedRequest.business_name}</span>
                                    </DetailItem>
                                    <DetailItem>
                                        <span className="label">Category</span>
                                        <span className="value">{selectedRequest.business_category || '-'}</span>
                                    </DetailItem>
                                    <DetailItem>
                                        <span className="label">Website</span>
                                        <span className="value">{selectedRequest.website || '-'}</span>
                                    </DetailItem>
                                    <DetailItem>
                                        <span className="label">Description</span>
                                        <span className="value">{selectedRequest.business_description || '-'}</span>
                                    </DetailItem>
                                </InfoCard>

                                <InfoCard>
                                    <DetailTitle>
                                        <PersonIcon sx={{ color: '#3b82f6' }} /> Contact Details
                                    </DetailTitle>
                                    <DetailItem>
                                        <span className="label">Full Name</span>
                                        <span className="value">{selectedRequest.contact_name}</span>
                                    </DetailItem>
                                    <DetailItem>
                                        <span className="label">Email Address</span>
                                        <span className="value">{selectedRequest.contact_email}</span>
                                    </DetailItem>
                                    <DetailItem>
                                        <span className="label">Phone Number</span>
                                        <span className="value">+91 {selectedRequest.contact_phone}</span>
                                    </DetailItem>
                                </InfoCard>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <InfoCard sx={{ mb: 3 }}>
                                    <DetailTitle>
                                        <LocationOnIcon sx={{ color: '#3b82f6' }} /> Business Location
                                    </DetailTitle>
                                    <DetailItem>
                                        <span className="label">Address</span>
                                        <span className="value">
                                            {selectedRequest.address_line1}
                                            {selectedRequest.address_line2 && <>, {selectedRequest.address_line2}</>}
                                        </span>
                                    </DetailItem>
                                    <DetailItem>
                                        <span className="label">City</span>
                                        <span className="value">{selectedRequest.city}</span>
                                    </DetailItem>
                                    <DetailItem>
                                        <span className="label">State / Region</span>
                                        <span className="value">{selectedRequest.state}</span>
                                    </DetailItem>
                                    <DetailItem>
                                        <span className="label">PIN Code</span>
                                        <span className="value">{selectedRequest.zip_code}</span>
                                    </DetailItem>
                                </InfoCard>

                                <InfoCard>
                                    <DetailTitle>
                                        <AssignmentIcon sx={{ color: '#3b82f6' }} /> Submission Status
                                    </DetailTitle>
                                    <DetailItem>
                                        <span className="label">Current Status</span>
                                        <span className="value">
                                            <StatusChip
                                                label={formatStatus(selectedRequest.status)}
                                                status={selectedRequest.status}
                                            />
                                        </span>
                                    </DetailItem>
                                    <DetailItem>
                                        <span className="label">Submitted On</span>
                                        <span className="value">{formatDate(selectedRequest.created_at)}</span>
                                    </DetailItem>
                                    <DetailItem>
                                        <span className="label">Phone on Board</span>
                                        <span className="value">{selectedRequest.phone_on_board ? 'Yes' : 'No'}</span>
                                    </DetailItem>
                                    {selectedRequest.location_url && (
                                        <Box sx={{ mt: 2 }}>
                                            <Button
                                                fullWidth
                                                variant="outlined"
                                                href={selectedRequest.location_url}
                                                target="_blank"
                                                startIcon={<LocationOnIcon />}
                                                sx={{
                                                    borderRadius: '10px',
                                                    textTransform: 'none',
                                                    borderColor: '#e2e8f0',
                                                    color: '#334155'
                                                }}
                                            >
                                                View on Google Maps
                                            </Button>
                                        </Box>
                                    )}
                                </InfoCard>
                            </Grid>

                            <Grid item xs={12}>
                                <InfoCard>
                                    <DetailTitle>
                                        <ImageIcon sx={{ color: '#3b82f6' }} /> Verification Gallery
                                    </DetailTitle>
                                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                                        <Grid item xs={12} sm={6}>
                                            <Box sx={{ p: 2, border: '1px solid #f1f5f9', borderRadius: '12px', backgroundColor: '#fcfcfc' }}>
                                                <Typography variant="subtitle2" sx={{ color: '#64748b', mb: 1.5, fontWeight: 600 }}>Visiting Card</Typography>
                                                {selectedRequest.visiting_card_image ? (
                                                    <Box
                                                        component="img"
                                                        src={selectedRequest.visiting_card_image}
                                                        sx={{
                                                            width: '100%',
                                                            height: '180px',
                                                            objectFit: 'cover',
                                                            borderRadius: '8px',
                                                            cursor: 'pointer',
                                                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                                            '&:hover': { transform: 'scale(1.02)' },
                                                            transition: 'transform 0.2s ease'
                                                        }}
                                                        onClick={() => window.open(selectedRequest.visiting_card_image, '_blank')}
                                                    />
                                                ) : <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>No visiting card provided</Typography>}
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Box sx={{ p: 2, border: '1px solid #f1f5f9', borderRadius: '12px', backgroundColor: '#fcfcfc' }}>
                                                <Typography variant="subtitle2" sx={{ color: '#64748b', mb: 1.5, fontWeight: 600 }}>Shop Front Board</Typography>
                                                {selectedRequest.shop_board_image ? (
                                                    <Box
                                                        component="img"
                                                        src={selectedRequest.shop_board_image}
                                                        sx={{
                                                            width: '100%',
                                                            height: '180px',
                                                            objectFit: 'cover',
                                                            borderRadius: '8px',
                                                            cursor: 'pointer',
                                                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                                            '&:hover': { transform: 'scale(1.02)' },
                                                            transition: 'transform 0.2s ease'
                                                        }}
                                                        onClick={() => window.open(selectedRequest.shop_board_image, '_blank')}
                                                    />
                                                ) : <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>No shop board provided</Typography>}
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </InfoCard>
                            </Grid>
                            {selectedRequest.admin_notes && (
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 1, mb: 2 }} />
                                    <InfoCard>
                                        <DetailTitle sx={{ color: '#3b82f6' }}>Admin Notes</DetailTitle>
                                        <Typography variant="body2" sx={{ color: '#475569', lineHeight: 1.6 }}>
                                            {selectedRequest.admin_notes}
                                        </Typography>
                                    </InfoCard>
                                </Grid>
                            )}
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setViewDialog(false)}>Close</Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            setViewDialog(false);
                            handleEditClick(selectedRequest);
                        }}
                    >
                        Update Status
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Status Dialog */}
            <Dialog
                open={editDialog}
                onClose={() => setEditDialog(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Update Request Status</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Update the progress of this GMB creation request and add any internal notes.
                    </Typography>
                    <Box sx={{ pt: 1 }}>
                        <FormControl fullWidth sx={{ mb: 3 }}>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={editStatus}
                                label="Status"
                                onChange={(e) => setEditStatus(e.target.value)}
                                sx={{ borderRadius: '10px' }}
                            >
                                <MenuItem value="pending_creation">Pending Creation</MenuItem>
                                <MenuItem value="in_progress">In Progress</MenuItem>
                                <MenuItem value="completed">Completed</MenuItem>
                                <MenuItem value="cancelled">Cancelled</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            label="Admin Notes"
                            value={editNotes}
                            onChange={(e) => setEditNotes(e.target.value)}
                            placeholder="Add notes about the verification process, issues encountered, etc."
                            sx={{
                                '& .MuiOutlinedInput-root': { borderRadius: '10px' }
                            }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button
                        onClick={() => setEditDialog(false)}
                        disabled={saving}
                        sx={{ borderRadius: '8px', textTransform: 'none', color: '#64748b' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveStatus}
                        disabled={saving}
                        sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            backgroundColor: '#3b82f6',
                            px: 3,
                            '&:hover': { backgroundColor: '#2563eb' }
                        }}
                    >
                        {saving ? <CircularProgress size={20} color="inherit" /> : 'Save Changes'}
                    </Button>
                </DialogActions>
            </Dialog>
        </PageContainer >
    );
};

export default GmbCreationRequests;
